import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { TlkCardComponent } from '@itero/ui-toolkit-angular/card';
import { TlkSlideToggleComponent } from '@itero/ui-toolkit-angular/slide-toggle';
import { ApplianceTypeEnums } from '@modules/appliance-details/models/appliance-type.enum';
import { TranslateModule } from '@ngx-translate/core';
import { BaseDestroyableComponent } from '@shared/base-classes/base-destroyable';
import { RxProcedureTypeControlComponent } from '@shared/components/rx-procedure-type-control/rx-procedure-type-control.component';
import { SharedModule } from '@shared/shared.module';
import { Observable } from 'rxjs';
import { shareReplay, takeUntil, tap } from 'rxjs/operators';
import { IntuitiveApplianceDetailsFacade, IntuitiveApplianceDetailsFormState } from '../../rx-intuitive-appliance-details.facade';
import { ProcedureSectionVisibilityService } from '@shared/services/procedure-section-visibility.service';

@Component({
	selector: 'rx-intuitive-appliance-details',
	templateUrl: './rx-intuitive-appliance-details.component.html',
	styleUrls: ['./rx-intuitive-appliance-details.component.scss'],
	providers: [IntuitiveApplianceDetailsFacade],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		SharedModule,
		TranslateModule,
		TlkSlideToggleComponent,
		TlkCardComponent,
		RxProcedureTypeControlComponent
	]
})
export class RxIntuitiveApplianceDetailsComponent extends BaseDestroyableComponent implements OnInit {
	private isReadOnly: boolean;

	applianceTypes$ = this.facade.applianceTypes$;
	/** Material type + arch toggles — only when EnableApplianceDetails SWO is on. */
	isExtendedApplianceDetailsVisible$ = this.procedureSectionVisibilityService.isApplianceDetailsVisible$;
	isReadOnly$: Observable<boolean> = this.facade.isReadonly$.pipe(
		tap(isReadOnly => (this.isReadOnly = isReadOnly)),
		shareReplay({ refCount: true, bufferSize: 1 })
	);
	isApplianceTypeVisible$ = this.facade.isApplianceTypeVisible$;
	isApplianceUpperReadonly$ = this.facade.isApplianceUpperReadonly$;
	isApplianceLowerReadonly$ = this.facade.isApplianceLowerReadonly$;
	procedureTitle$ = this.facade.procedureTitle$;

	form = this.formBuilder.nonNullable.group<IntuitiveApplianceDetailsFormState>({
		applianceType: { Id: ApplianceTypeEnums.Empty, Name: '' },
		isUpperChecked: false,
		isLowerChecked: false
	});

	get applianceTypeControl() {
		return this.form.controls.applianceType;
	}

	get isUpperControl() {
		return this.form.controls.isUpperChecked;
	}

	get isLowerControl() {
		return this.form.controls.isLowerChecked;
	}

	constructor(
		private readonly facade: IntuitiveApplianceDetailsFacade,
		private readonly formBuilder: FormBuilder,
		private readonly procedureSectionVisibilityService: ProcedureSectionVisibilityService
	) {
		super();
	}

	ngOnInit(): void {
		this.loadFromStore();
		this.subscribeToRxLoaded();
		this.subscribeToUserChanges();
	}

	switchOffControl(event: boolean, control: FormControl) {
		if (!event && !this.isReadOnly) {
			control.patchValue(false);
		}
	}

	private loadFromStore(): void {
		this.form.patchValue(this.facade.storeState, { emitEvent: false });
	}

	private subscribeToRxLoaded(): void {
		this.facade
			.getLoadedRx()
			.pipe(
				tap(state => this.form.patchValue(state, { emitEvent: false })),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	private subscribeToUserChanges(): void {
		this.form.valueChanges
			.pipe(
				tap(() => this.facade.updateApplianceDetails(this.form.getRawValue())),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}
}
